/**
 * Shared landing-route resolver (D45b Phase 1).
 *
 * Three sites decide where an authenticated member lands: the root `/`
 * index redirect, the authenticated-on-`/login` redirect, and the
 * quiz-completion gate-release redirect. All three must stay in lock-step
 * as the Dashboard rollout flag flips, so they all call this one function
 * instead of hand-editing three independent redirect targets.
 *
 * Flag resolution (two layers):
 *  1. `VITE_DASHBOARD_DEFAULT_LANDING === 'true'` — build-time default,
 *     OFF in every real environment today. D51 is the deliverable that
 *     will eventually flip this to `'true'` in production.
 *  2. A `localStorage` runtime override (`ff_dashboardDefaultLanding`),
 *     read ONLY when `import.meta.env.MODE !== 'production'`. This exists
 *     solely so Playwright can flip the flag per test context via
 *     `page.addInitScript()` without a second build or a second
 *     `webServer` (the project runs a single shared Playwright project).
 *     The `MODE` guard makes the override structurally unreachable in a
 *     production build regardless of what anyone sets via devtools.
 *
 * There is deliberately NO query-param override. A query param would be
 * trivially shareable (`?ff_dashboardDefaultLanding=true`) and would let
 * any member activate the pre-D51 placeholder Dashboard in production via
 * a shared link — exactly the exposure CD's D2 decision closed. Do not
 * add one.
 */

/** localStorage key for the non-production runtime override. */
export const DASHBOARD_LANDING_OVERRIDE_KEY = 'ff_dashboardDefaultLanding'

/**
 * Fallback target when the Dashboard-default-landing flag is off — each
 * call site passes its own today's-behavior target so this module never
 * has to hardcode which of `/search`/`/feed` a given site means.
 */
export type LandingFallback = '/search' | '/feed'

export type LandingTarget = LandingFallback | '/dashboard'

/**
 * Resolves whether the Dashboard-default-landing flag is currently on.
 * Pure except for reading `import.meta.env` (build-time constant) and,
 * outside production, `localStorage` (runtime override for testing).
 */
export function isDashboardDefaultLandingEnabled(): boolean {
  if (import.meta.env.VITE_DASHBOARD_DEFAULT_LANDING === 'true') {
    return true
  }

  // Structurally unreachable in a production build — the override is
  // never read there, regardless of what's in localStorage.
  if (import.meta.env.MODE !== 'production') {
    try {
      if (localStorage.getItem(DASHBOARD_LANDING_OVERRIDE_KEY) === 'true') {
        return true
      }
    } catch {
      // localStorage unavailable (private browsing, disabled storage, SSR-ish
      // context) — fall through to the build-time default.
    }
  }

  return false
}

/**
 * Single shared resolver consumed by all three landing-redirect sites
 * (`router/routes/index.tsx`, `router/routes/login.tsx`,
 * `pages/quizzes/QuizTaking.tsx`'s gate-release redirect).
 *
 * With the flag off (the default), unauthenticated and authenticated
 * members alike land on `fallback` — today's behavior, unchanged. With
 * the flag on, authenticated members land on `/dashboard`; unauthenticated
 * visitors always resolve to `fallback` regardless of flag state, since
 * Dashboard is an authenticated-only surface.
 */
export function resolveLandingRoute(isAuthenticated: boolean, fallback: LandingFallback): LandingTarget {
  if (isAuthenticated && isDashboardDefaultLandingEnabled()) {
    return '/dashboard'
  }
  return fallback
}
